import type { ReactNode } from 'react';
import clsx from 'clsx';
import React, { useState } from 'react';
import { Link } from 'wouter';

import { Icon } from '@fragment/ui/src/components/Icon';
import { POSTHOG_MASK_CLASS } from '@fragment/ui/src/constants';
import { useInlineCopy } from '@fragment/ui/src/hooks/useInlineCopy';

export const DetailCellWithCopy = ({
  value,
  textToCopy,
}: {
  value: ReactNode;
  textToCopy: string;
}) => {
  const { handler, isCopied } = useInlineCopy({
    textToCopy,
    onCopyError: () => {},
  });
  return (
    <button
      className="flex flex-row justify-between text-left group min-w-f0 w-full hover:bg-negative hover:cursor-pointer"
      onClick={handler}
      type="button"
    >
      <div className={clsx(POSTHOG_MASK_CLASS, 'grow text-main truncate')}>
        {value}
      </div>
      <div className="hidden group-hover:block w-[6ch] text-right text-main-500">
        {isCopied ? 'Copied' : 'Copy'}
      </div>
    </button>
  );
};
const DetailCellWithLink = ({
  value,
  linkHref,
}: {
  value: ReactNode;
  linkHref: string;
}) => (
  <div className="flex flex-row text-left group min-w-f0 w-full hover:bg-negative hover:cursor-pointer">
    <Link to={linkHref}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- wouter's Link clones its href onto this anchor */}
      <a
        className={clsx(
          POSTHOG_MASK_CLASS,
          'grow min-w-f0 text-main flex flex-row justify-between gap-x-[1ch]'
        )}
      >
        <div className="min-w-f0 truncate">{value}</div>
        <span className="invisible group-hover:visible shrink-0" aria-hidden>
          <Icon type="right" size="md" />
        </span>
      </a>
    </Link>
  </div>
);

const BasicDetailCell = ({
  value,
  truncate = true,
}: {
  value: ReactNode;
  truncate?: boolean;
}) => (
  <div className="flex flex-row justify-between group min-w-f0 w-full hover:cursor-text">
    <div
      className={clsx(
        POSTHOG_MASK_CLASS,
        'grow text-main',
        truncate && 'truncate'
      )}
    >
      {value}
    </div>
  </div>
);

type DetailCellProps = {
  value: ReactNode;
  textToCopy?: string;
  linkHref?: string;
  truncate?: boolean;
};

const DetailCell = ({
  value,
  textToCopy,
  linkHref,
  truncate = true,
}: DetailCellProps) => {
  if (textToCopy && linkHref) {
    throw new Error('Cannot have both textToCopy and linkHref');
  }

  if (textToCopy) {
    return <DetailCellWithCopy value={value} textToCopy={textToCopy} />;
  }

  if (linkHref) {
    return <DetailCellWithLink value={value} linkHref={linkHref} />;
  }

  return <BasicDetailCell value={value} truncate={truncate} />;
};

const DetailKVCellWithCopy = ({
  cellKey,
  value,
  textToCopy,
  renderKey = true,
}: {
  cellKey: string;
  value: ReactNode;
  textToCopy: string;
  renderKey?: boolean;
}) => {
  const { handler, isCopied } = useInlineCopy({
    textToCopy,
    onCopyError: () => {},
  });
  return (
    <div className="flex flex-row">
      {renderKey && (
        <div
          className={clsx(
            POSTHOG_MASK_CLASS,
            'w-1/2 shrink-0 border-r-[0.5px] border-main-300 truncate'
          )}
        >
          {cellKey}
        </div>
      )}
      <button
        className="flex flex-row justify-between text-left group min-w-f0 w-full hover:bg-negative hover:cursor-pointer h-[1lh]"
        onClick={handler}
        type="button"
      >
        <div className={clsx(POSTHOG_MASK_CLASS, 'grow text-main truncate')}>
          {value}
        </div>
        <div className="invisible group-hover:visible w-[6ch] text-right text-main-500">
          {isCopied ? 'Copied' : 'Copy'}
        </div>
      </button>
    </div>
  );
};

export const DetailHeaderRow = ({
  label,
  value,
  textToCopy,
  linkHref,
  truncate = true,
}: {
  label: ReactNode;
  value: ReactNode;
  textToCopy?: string;
  linkHref?: string;
  truncate?: boolean;
}) => (
  <div className="flex flex-row border-[0.5px] border-main-300 border-b-0 last:border-b-[0.5px]">
    <div className="w-f16 shrink-0 text-main-500 border-r-[0.5px] border-main-300">
      {label}
    </div>
    <DetailCell
      value={value}
      textToCopy={textToCopy}
      linkHref={linkHref}
      truncate={truncate}
    />
  </div>
);

export const DetailHeaderSection = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col first:pt-0 pt-f2">{children}</div>
);

export const DetailHeader = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col w-full">{children}</div>
);

export const DetailMultiRowLabel = ({ label }: { label: ReactNode }) => (
  <div className="w-f16 shrink-0 text-main-500 border-[0.5px] border-r-0 border-main-300 align-top line-clamp-1 h-min">
    {label}
  </div>
);

export const DetailMultiValueRow = ({
  label,
  values,
}: {
  label: ReactNode;
  values: {
    nodeKey: string;
    value: ReactNode;
    textToCopy?: string;
    linkHref?: string;
  }[];
}) => (
  <div className="flex flex-row">
    <DetailMultiRowLabel label={label} />
    <div className="min-w-f0 w-full">
      {values.map(({ value, nodeKey, textToCopy, linkHref }) => (
        <div
          className="border-[0.5px] border-main-300 border-b-0 last:border-b-[0.5px]"
          key={nodeKey}
        >
          <DetailCell
            value={value}
            textToCopy={textToCopy}
            linkHref={linkHref}
          />
        </div>
      ))}
    </div>
  </div>
);

export const DetailMultiKVRow = ({
  label,
  values,
}: {
  label: ReactNode;
  values: {
    key: string;
    value: ReactNode;
    textToCopy: string;
    renderKey: boolean;
  }[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const buttonTextColor = isExpanded ? 'text-main-500' : 'text-main';
  const buttonBorder = isExpanded ? 'border-b-0' : '';
  return (
    <div className="flex flex-row">
      <DetailMultiRowLabel label={label} />
      <div className="min-w-f0 w-full">
        <button
          className={`flex flex-row justify-between text-left min-w-f0 w-full items-center hover:bg-negative hover:cursor-pointer hover:text-main border-[0.5px] border-main-300 ${buttonBorder} ${buttonTextColor}`}
          onClick={() => setIsExpanded(!isExpanded)}
          type="button"
        >
          {isExpanded ? 'Hide' : `Show (${values.length})`}
          {isExpanded ? (
            <Icon type="up" size="md" primaryColor="gray" />
          ) : (
            <Icon type="down" size="md" primaryColor="gray" />
          )}
        </button>
        {isExpanded &&
          values.map(({ key, value, textToCopy, renderKey }) => (
            <div
              key={key}
              className="border-[0.5px] text-main-500 border-main-300 border-b-0 last:border-b-[0.5px]"
            >
              <DetailKVCellWithCopy
                cellKey={key}
                value={value}
                textToCopy={textToCopy}
                renderKey={renderKey}
              />
            </div>
          ))}
      </div>
    </div>
  );
};
